import { useEffect, useMemo, useState } from 'react'
import { message } from 'antd'
import { SegmentControl } from '@forwardflow/ui-v2'
import {
   MailIcon,
   Message01Icon,
   PhoneIcon,
   PhoneRotaryIcon,
   User01Icon,
} from '@forwardflow/icons'
import { campaignV2Api } from '../../services'
import { useCatalog } from '../../queries/campaign-v2.queries'
import {
   contentDraftKey,
   useCampaignV2Store,
   TContentDraft,
} from '../../store/editor.store'
import {
   ICatalogItem,
   IContentReference,
   IContentRevision,
   TContentDefinition,
} from '../../interfaces/campaign-v2.types'
import { channelsUsed } from '../../utils/config'
import {
   defaultMessageGroupName,
   defaultPersonaName,
   defaultReplyInstructionsName,
   defaultScriptName,
} from '../../utils/names'
import {
   EmptyNote,
   IStepFooter,
   ListCard,
   ListRow,
   Pill,
   StepFrame,
   TextButton,
} from '../shared'
import { ContentPreview } from '../content/ContentPreview'
import { ContentEditor } from '../content/ContentEditor'
import { blankDefinition } from '../content/blank'
import { ContentPicker } from '../content/ContentPicker'
import {
   SLOT_KIND,
   summaryOfItem,
   TSlot,
   useContentActions,
} from '../content/useContentActions'

interface ISlotMeta {
   slot: TSlot
   title: string
   hint: string
   icon: JSX.Element
   direction?: 'outbound' | 'inbound'
   required: (used: ReturnType<typeof channelsUsed>) => boolean
   addLabel: string
}

const SLOTS: ISlotMeta[] = [
   {
      slot: 'outboundScripts',
      title: 'Outgoing call script',
      hint: 'What the assistant says when it calls a lead.',
      icon: <PhoneIcon width={15} />,
      direction: 'outbound',
      required: (u) => u.call,
      addLabel: 'Choose an outgoing script',
   },
   {
      slot: 'inboundScripts',
      title: 'Incoming call script',
      hint: 'How calls to the campaign numbers are answered. Unknown callers use the organization’s answering configuration.',
      icon: <PhoneRotaryIcon width={15} />,
      direction: 'inbound',
      required: () => true,
      addLabel: 'Choose an incoming script',
   },
   {
      slot: 'strategies',
      title: 'Outbound content groups',
      hint: 'Texts, emails and voicemails in order. The cadence decides when each one goes out.',
      icon: <Message01Icon width={15} />,
      required: (u) => u.sms || u.email || u.voicemail,
      addLabel: 'Add an outbound content group',
   },
   {
      slot: 'conversationContent',
      title: 'Reply instructions',
      hint: 'How the assistant answers when a lead texts back. Separate from the scheduled messages.',
      icon: <MailIcon width={15} />,
      required: () => true,
      addLabel: 'Choose reply instructions',
   },
   {
      slot: 'humanScripts',
      title: 'Human call guides',
      hint: 'Guidance for your agents when they speak with a lead.',
      icon: <User01Icon width={15} />,
      required: () => false,
      addLabel: 'Choose a human call guide',
   },
   {
      slot: 'personas',
      title: 'Persona guidance',
      hint: 'Optional. Without one, the organization’s default guidance applies.',
      icon: <User01Icon width={15} />,
      required: () => false,
      addLabel: 'Choose persona guidance',
   },
]

/**
 * Step 2 — what we say. One pool per category: call scripts, message
 * groups, reply instructions and an optional personality. Everything is a
 * reference to a library item; this step binds, customizes and writes
 * them. Strategy names are generated, never asked for.
 */
export const ContentStep = ({
   footer,
   poolId: selectedPoolId,
   slot: selectedSlot,
   draftOnly = false,
}: {
   footer?: IStepFooter
   poolId?: string
   slot?: TSlot
   draftOnly?: boolean
}) => {
   const {
      config,
      name,
      categoryLabels,
      contentIndex,
      indexContent,
      contentDrafts,
      setContentDraft,
   } = useCampaignV2Store()
   const pools = useMemo(
      () => config?.contentPools || [],
      [config?.contentPools]
   )
   const [poolId, setPoolId] = useState(pools[0]?.id || '')
   useEffect(() => {
      if (!pools.some((p) => p.id === poolId) && pools[0])
         setPoolId(pools[0].id)
   }, [pools, poolId])
   const pool =
      pools.find((p) => p.id === (selectedPoolId || poolId)) || pools[0]
   const used = useMemo(
      () => channelsUsed(config?.cadence || []),
      [config?.cadence]
   )
   const actions = useContentActions(pool?.id || '')
   const draftKey = contentDraftKey(pool?.id || '', selectedSlot)
   const open = contentDrafts[draftKey] || null
   const setOpen = (draft: TContentDraft | null) =>
      setContentDraft(draftKey, draft)
   const [preview, setPreview] = useState<{
      slot: TSlot
      ref: IContentReference
      base: IContentRevision
      shared: boolean
   } | null>(null)
   const [loadingEdit, setLoadingEdit] = useState<string | null>(null)

   const categoryLabel = pool ? categoryLabels[pool.businessTypeId] : undefined

   // Names for references bound before this session (e.g. an existing draft).
   useCatalogIndex(pool?.businessTypeId || null)

   const missing = SLOTS.filter(
      (s) => s.required(used) && pool && !(pool[s.slot] || []).length
   )
   const allPoolsOk = pools.every((p) =>
      SLOTS.filter((s) => s.required(used)).every(
         (s) => (p[s.slot] || []).length
      )
   )

   const nextName = (slot: TSlot) => {
      const n = (pool?.[slot] || []).length + 1
      switch (slot) {
         case 'strategies':
            return defaultMessageGroupName(name, categoryLabel, n)
         case 'outboundScripts':
            return defaultScriptName(name, categoryLabel, 'outbound', n)
         case 'humanScripts':
            return defaultScriptName(name, categoryLabel, 'outbound', n)
         case 'inboundScripts':
            return defaultScriptName(name, categoryLabel, 'inbound', n)
         case 'conversationContent':
            return defaultReplyInstructionsName(name, categoryLabel)
         case 'personas':
            return defaultPersonaName(name, categoryLabel)
      }
   }

   const openEditor = async (
      slot: TSlot,
      ref: IContentReference,
      viewOnly = false
   ) => {
      setLoadingEdit(ref.id)
      try {
         const base = await campaignV2Api().readRevision(
            ref.revisionId,
            ref.id,
            true
         )
         const shared =
            contentIndex[`${SLOT_KIND[slot]}:${ref.id}`]?.shared ?? true
         if (viewOnly) setPreview({ slot, ref, base, shared })
         else {
            setPreview(null)
            setOpen({ mode: 'edit', slot, ref, base, shared })
         }
      } catch (e) {
         message.error((e as Error).message || 'Could not open that item')
      } finally {
         setLoadingEdit(null)
      }
   }

   const customizeFromPicker = async (
      slot: TSlot,
      item: ICatalogItem,
      viewOnly = false
   ) => {
      setLoadingEdit(String(item.contentId))
      try {
         const base = item.revisionId
            ? await campaignV2Api().readRevision(
                 item.revisionId,
                 String(item.contentId),
                 true
              )
            : await campaignV2Api().acquireRevision(item.kind, item.contentId)
         indexContent([summaryOfItem(item)])
         if (viewOnly) {
            setPreview({
               slot,
               ref: { id: String(base.contentId), revisionId: base.id },
               base,
               shared: item.shared,
            })
            return
         }
         const def = {
            ...base.definition,
            name: nextName(slot),
         } as TContentDefinition
         setOpen({
            mode: 'edit',
            slot,
            ref: { id: String(base.contentId), revisionId: base.id },
            base: { ...base, definition: def },
            shared: true,
         })
      } catch (e) {
         message.error((e as Error).message || 'Could not open that template')
      } finally {
         setLoadingEdit(null)
      }
   }

   if (!config || !pool) return null

   const slotMeta = open ? SLOTS.find((s) => s.slot === open.slot)! : null

   return (
      <StepFrame
         eyebrow="Step 2 of 8 · Content"
         title={
            selectedSlot
               ? SLOTS.find((s) => s.slot === selectedSlot)?.title || 'Content'
               : 'What should the assistant say?'
         }
         lede="Pick from the library or write your own. Shared templates stay untouched; anything you change becomes your own copy."
         footer={
            footer
               ? {
                    ...footer,
                    disabled: footer.disabled || !allPoolsOk || Boolean(open),
                    action: open
                       ? 'Finish editing first'
                       : allPoolsOk
                         ? footer.action
                         : `Add ${missing[0]?.title.toLowerCase() || 'the required content'}`,
                 }
               : undefined
         }
         testId="step-content"
      >
         {!selectedPoolId && pools.length > 1 && (
            <SegmentControl
               value={pool.id}
               onChange={setPoolId}
               data={pools.map((p) => ({
                  value: p.id,
                  label: categoryLabels[p.businessTypeId] || p.id,
               }))}
               data-testid="pool-tabs"
            />
         )}

         {preview && (
            <ContentPreview
               revision={preview.base}
               shared={draftOnly || preview.shared}
               onClose={() => setPreview(null)}
               onEdit={() => {
                  setOpen({ mode: 'edit', ...preview })
                  setPreview(null)
               }}
            />
         )}

         {!preview && open?.mode === 'pick' && slotMeta && (
            <ContentPicker
               kind={SLOT_KIND[open.slot]}
               categoryId={pool.businessTypeId}
               direction={slotMeta.direction}
               callerType={open.slot === 'humanScripts' ? 'human' : 'ai'}
               exclude={(pool[open.slot] || []).map((r) => r.id)}
               title={slotMeta.addLabel}
               busy={actions.busy || Boolean(loadingEdit)}
               onUse={async (item) => {
                  const ref = await actions.useExisting(open.slot, item)
                  if (ref) setOpen(null)
               }}
               onPreview={(item) =>
                  void customizeFromPicker(open.slot, item, true)
               }
               onCustomize={(item) => void customizeFromPicker(open.slot, item)}
               onWrite={() =>
                  setOpen({
                     mode: 'write',
                     slot: open.slot,
                     def: blankDefinition(
                        SLOT_KIND[open.slot],
                        nextName(open.slot),
                        pool.businessTypeId,
                        slotMeta.direction,
                        open.slot === 'humanScripts' ? 'human' : 'ai'
                     ),
                  })
               }
               onClose={() => setOpen(null)}
            />
         )}

         {!preview && open?.mode === 'write' && slotMeta && (
            <ContentEditor
               kind={SLOT_KIND[open.slot]}
               key={draftKey + ':write'}
               initial={open.def}
               onDraftChange={(def) => setOpen({ ...open, def })}
               shared={false}
               title={`New: ${slotMeta.title.toLowerCase()}`}
               lede={slotMeta.hint}
               slotDirection={slotMeta.direction}
               saving={actions.busy}
               onCancel={() => {
                  if (window.confirm('Discard this unsaved content?'))
                     setOpen({ mode: 'pick', slot: open.slot })
               }}
               onSave={async (def) => {
                  const ref = await actions.createNew(
                     open.slot,
                     SLOT_KIND[open.slot],
                     def
                  )
                  if (ref) setOpen(null)
               }}
            />
         )}

         {!preview && open?.mode === 'edit' && slotMeta && (
            <ContentEditor
               kind={open.base.kind}
               key={draftKey + ':' + open.ref.revisionId}
               initial={open.def || open.base.definition}
               onDraftChange={(def) => setOpen({ ...open, def })}
               shared={draftOnly || open.shared}
               title={open.base.definition.name}
               lede={slotMeta.hint}
               slotDirection={slotMeta.direction}
               saving={actions.busy}
               onCancel={() => {
                  if (window.confirm('Discard this unsaved content?'))
                     setOpen(null)
               }}
               onSave={async (def) => {
                  const bound = (pool[open.slot] || []).some(
                     (r) => r.id === open.ref.id
                  )
                  const ref = bound
                     ? await actions.saveEdits(
                          open.slot,
                          open.ref,
                          draftOnly || open.shared,
                          open.base,
                          def
                       )
                     : await actions.saveEdits(
                          open.slot,
                          open.ref,
                          true,
                          open.base,
                          def
                       )
                  if (ref) setOpen(null)
               }}
            />
         )}

         {!preview &&
            !open &&
            SLOTS.filter((s) => !selectedSlot || s.slot === selectedSlot).map(
               (s) => {
                  const refs = pool[s.slot] || []
                  const required = s.required(used)
                  return (
                     <ListCard
                        key={s.slot}
                        title={
                           <span className="flex items-center gap-2">
                              {s.title}
                              {required ? (
                                 refs.length ? (
                                    <Pill tone="ok">ready</Pill>
                                 ) : (
                                    <Pill tone="warn">needed</Pill>
                                 )
                              ) : (
                                 <Pill>optional</Pill>
                              )}
                           </span>
                        }
                        hint={s.hint}
                        testId={`slot-${s.slot}`}
                     >
                        {!refs.length && (
                           <EmptyNote>
                              {s.slot === 'strategies' && !required
                                 ? 'Your cadence has no texts or emails, so no group is needed.'
                                 : 'Nothing chosen yet.'}
                           </EmptyNote>
                        )}
                        {refs.map((r) => {
                           const info =
                              contentIndex[`${SLOT_KIND[s.slot]}:${r.id}`]
                           return (
                              <ListRow
                                 key={r.id}
                                 icon={s.icon}
                                 title={info?.name || `Item ${r.id}`}
                                 sub={describeRef(info)}
                                 right={
                                    <>
                                       {info && (
                                          <Pill
                                             tone={
                                                info.shared ? 'muted' : 'purple'
                                             }
                                          >
                                             {info.shared
                                                ? 'template'
                                                : 'yours'}
                                          </Pill>
                                       )}
                                       <TextButton
                                          onClick={() =>
                                             void openEditor(s.slot, r, true)
                                          }
                                          disabled={Boolean(loadingEdit)}
                                          testId={`view-${r.id}`}
                                       >
                                          View
                                       </TextButton>
                                       <TextButton
                                          onClick={() =>
                                             void openEditor(s.slot, r)
                                          }
                                          tone="muted"
                                          disabled={loadingEdit === r.id}
                                          testId={`edit-${r.id}`}
                                       >
                                          {loadingEdit === r.id
                                             ? 'Opening…'
                                             : info?.shared === false
                                               ? 'Edit'
                                               : 'Customize'}
                                       </TextButton>
                                       <TextButton
                                          onClick={() =>
                                             actions.unbind(s.slot, r.id)
                                          }
                                          tone="danger"
                                          testId={`unbind-${r.id}`}
                                       >
                                          Remove
                                       </TextButton>
                                    </>
                                 }
                                 testId={`ref-${r.id}`}
                              />
                           )
                        })}
                        <button
                           type="button"
                           onClick={() =>
                              setOpen({ mode: 'pick', slot: s.slot })
                           }
                           className="w-full flex items-center gap-3 px-4 h-11 text-left text-sm font-medium text-[#A684FF] hover:bg-[#9F7BFF]/[0.07]"
                           data-testid={`add-${s.slot}`}
                        >
                           <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#9F7BFF]/15 text-[#C9B8FF] text-sm leading-none">
                              +
                           </span>
                           {s.addLabel}
                        </button>
                     </ListCard>
                  )
               }
            )}
      </StepFrame>
   )
}

const describeRef = (info?: {
   kind: string
   counts?: { sms: number; email: number; voicemail: number }
   leadTypes?: string[]
   callDirections?: string[]
}) => {
   if (!info) return 'Loading details…'
   if (info.kind === 'strategy' && info.counts) {
      const c = info.counts
      return (
         [
            c.sms ? `${c.sms} text${c.sms === 1 ? '' : 's'}` : '',
            c.email ? `${c.email} email${c.email === 1 ? '' : 's'}` : '',
            c.voicemail
               ? `${c.voicemail} voicemail${c.voicemail === 1 ? '' : 's'}`
               : '',
         ]
            .filter(Boolean)
            .join(' · ') || 'Empty group'
      )
   }
   if (info.kind === 'phone') {
      return info.leadTypes?.includes('unknown')
         ? 'Greets known leads and unknown callers'
         : 'Known leads only'
   }
   return undefined
}

/** Fill the content index from the catalog so existing drafts show names. */
const useCatalogIndex = (categoryId: number | null) => {
   const { indexContent } = useCampaignV2Store()
   const phone = useCatalog('phone', categoryId)
   const strategy = useCatalog('strategy', categoryId)
   const conversation = useCatalog('conversation', categoryId)
   const persona = useCatalog('persona', categoryId)
   useEffect(() => {
      const items = [
         ...(phone.data || []),
         ...(strategy.data || []),
         ...(conversation.data || []),
         ...(persona.data || []),
      ]
      if (items.length) indexContent(items.map(summaryOfItem))
   }, [
      phone.data,
      strategy.data,
      conversation.data,
      persona.data,
      indexContent,
   ])
}
